import { Text } from '@/components/@base/typography';
import { TEXT_TYPE } from '@/components/@base/typography/text/type';
import { MARKETS, PRODUCT_TABS } from '@/components/app/product/components/@shared/type';

import BundleModel from '@/models/bundle.model';
import ProductModel from '@/models/product.model';

export function isBundle(data: BundleModel | ProductModel): data is BundleModel {
  if (data.market === MARKETS.BUNDLE) {
    return true;
  }

  return (data as BundleModel).bundleItems !== undefined;
}

// Product routes are always `/web/{slug}` or `/web/{slug}/{tab}`, so the tab
// is derived positionally instead of by prop-drilling `initialTab`, which lets
// the tabs UI stay mounted across `/web/{slug}` <-> `/web/{slug}/{tab}` navigations.
export function getTabFromPathname(pathname: string): PRODUCT_TABS {
  const tabSegment = pathname.split('/').filter(Boolean)[2];
  return Object.values(PRODUCT_TABS).includes(tabSegment as PRODUCT_TABS)
    ? (tabSegment as PRODUCT_TABS)
    : PRODUCT_TABS.CONTENT;
}

type LifetimePricingInput = Pick<
  ProductModel,
  'regularPrice' | 'salePrice' | 'discount' | 'price' | 'lastPrice' | 'isSubscription'
>;

export function getLifetimePricing(data: LifetimePricingInput) {
  // `regularPrice` is the subscription lifetime plan's own dedicated field — only reliable for
  // subscription products (see the round 1-6 history in pricing-discount-architecture memory).
  // A genuinely non-subscription product (normal/bundle/ready-site) has no such plan-specific
  // field to disambiguate; its native `lastPrice` is the correct "before discount" source.
  const lastPrice = data.isSubscription ? data.regularPrice || 0 : data.lastPrice || 0;
  // const price = lastPrice - (data.salePrice || 0);
  // const price = data.salePrice;
  const price = data.price;
  return { price, lastPrice, discount: data.discount || 0 };
}

type OneYearSubscriptionPricingInput = Pick<
  ProductModel,
  | 'subscriptionPriceOneYear'
  | 'salePriceSubscriptionOneYear'
  | 'discountSubscriptionOneYear'
  | 'percentTypeSubscriptionOneYear'
  | 'price'
  | 'saleEndedAt'
>;

export function getOneYearSubscriptionPricing(data: OneYearSubscriptionPricingInput) {
  const currentTimeInSeconds = Math.floor(Date.now() / 1000);
  // The one-year plan's own field takes priority over the generic `price` field, and `??` (not
  // `||`) so a genuinely-free plan (subscriptionPriceOneYear === 0) isn't clobbered by the
  // fallback. `hasAnnualDiscount` requires a real discount amount to exist, not just an unexpired
  // `saleEndedAt`: without it, a product with no one-year plan configured at all (both
  // subscriptionPriceOneYear and salePriceSubscriptionOneYear null) could still read as "on sale"
  // whenever saleEndedAt happens to be a future/placeholder timestamp, and `salePriceSubscriptionOneYear
  // || 0` would then render the plan as free even though it doesn't exist.
  const lastPrice = data.subscriptionPriceOneYear ?? data.price ?? 0;
  const hasAnnualDiscount =
    (data.discountSubscriptionOneYear ?? 0) > 0 && data.salePriceSubscriptionOneYear != null;
  const discountIsValid = hasAnnualDiscount && data.saleEndedAt > currentTimeInSeconds;
  const price = discountIsValid ? data.salePriceSubscriptionOneYear : lastPrice;
  const discount = discountIsValid ? (data.discountSubscriptionOneYear ?? 0) : 0;

  return { price, lastPrice, discount };
}

export function isAnnualDefault(
  data: Pick<
    ProductModel,
    | 'subscriptionDefaultPriceIsOneYear'
    | 'regularPrice'
    | 'isSubscription'
    | 'subscriptionPriceOneYear'
  >,
): boolean {
  // A null/undefined subscriptionPriceOneYear means the one-year plan isn't actually
  // configured for this product, regardless of what subscriptionDefaultPriceIsOneYear says —
  // fall back to lifetime rather than computing a phantom (often zero/"free") annual price.
  const isOneYear =
    !!data.isSubscription &&
    data.subscriptionPriceOneYear != null &&
    (data.subscriptionDefaultPriceIsOneYear === true ||
      !data.regularPrice ||
      data.regularPrice === 0);
  return isOneYear;
}

type DefaultPricingInput = LifetimePricingInput &
  OneYearSubscriptionPricingInput &
  Pick<ProductModel, 'subscriptionDefaultPriceIsOneYear' | 'isSubscription'>;

export function getDefaultPricing(data: DefaultPricingInput) {
  if (isAnnualDefault(data)) {
    return getOneYearSubscriptionPricing(data);
  }
  // The backend claims annual is the default plan (`subscriptionDefaultPriceIsOneYear: true`) but
  // there's no annual data to back it up, so `isAnnualDefault()` already fell back to lifetime —
  // however the generic `price` field `getLifetimePricing()` normally trusts was itself computed
  // by the backend *for that same (missing) annual model*, so it can't be trusted here either.
  // Confirmed live on `ithemes-security-pro` across two endpoints, wrong in two different ways
  // (`price: undefined` on the single-product endpoint, `price: 0` on the archive-list endpoint)
  // while `regularPrice` was correctly `10000` on both. Scoped narrowly to exactly this case —
  // an earlier attempt at fixing this by changing `getLifetimePricing()`'s formula globally (to
  // prefer `salePrice`) broke ordinary lifetime products, where `salePrice` is apparently not a
  // reliable "current price" source.
  const isForcedLifetimeFallback =
    !!data.isSubscription &&
    data.subscriptionDefaultPriceIsOneYear === true &&
    data.subscriptionPriceOneYear == null;
  if (isForcedLifetimeFallback) {
    const lastPrice = data.regularPrice || 0;
    return { price: lastPrice, lastPrice, discount: 0 };
  }
  return getLifetimePricing(data);
}

export function formatParagraphNewLine(text: string): React.ReactElement[] {
  return text.split('\n').map((str) => <Text type={TEXT_TYPE.P}>{str}</Text>);
}
